import { useCallback, useMemo, useState } from 'react';
import { useInstantSearch, usePagination } from 'react-instantsearch';

import { removePageParam } from './removePageParam';

/**
 * Type pour un élément de page dans la pagination
 */
export type PaginationItem = { type: 'page'; page: number };

/**
 * Hook pour encapsuler toute la logique de pagination
 * @param padding - Nombre de pages à afficher autour de la page courante (défaut: 2)
 * @param onPageChange - Callback appelé lors du changement de page
 */
export function usePaginationLogic(padding = 2, onPageChange?: () => void) {
  const [showMultiples, setShowMultiples] = useState(false);

  // Récupère les résultats et calcule le nombre réel de pages
  const { results } = useInstantSearch();
  const nbHits = results?.nbHits ?? 0;
  const hitsPerPage = results?.hitsPerPage || 20;
  const realNbPages = hitsPerPage > 0 ? Math.ceil(nbHits / hitsPerPage) : 0;

  const {
    currentRefinement,
    isFirstPage,
    isLastPage,
    refine,
    createURL: originalCreateURL,
  } = usePagination({ padding });

  /**
   * Calcule manuellement les pages à afficher avec padding
   * Car usePagination().pages ne retourne pas le bon nombre
   */
  const pages = useMemo(() => {
    if (realNbPages === 0) return [];

    const start = Math.max(0, currentRefinement - padding);
    const end = Math.min(realNbPages - 1, currentRefinement + padding);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentRefinement, padding, realNbPages]);

  /**
   * Navigue vers une page et déclenche le callback
   */
  const navigateToPage = useCallback(
    (pageNumber: number) => {
      refine(pageNumber);
      onPageChange?.();
    },
    [refine, onPageChange],
  );

  /**
   * Wrapper autour de createURL pour retirer le paramètre page si c'est la page 1
   */
  const createURL = useCallback(
    (pageNumber: number) => {
      const url = originalCreateURL(pageNumber);

      // Première page (index 0) : pas de paramètre de page dans le lien
      return pageNumber === 0 ? removePageParam(url) : url;
    },
    [originalCreateURL],
  );

  /**
   * Convertit une URL absolue en URL relative
   * Important pour éviter les hydration mismatches avec Next.js
   */
  const toRelativeURL = useCallback((url: string) => {
    try {
      const urlObj = new URL(
        url,
        typeof window !== 'undefined'
          ? window.location.href
          : 'http://localhost',
      );
      return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
    } catch {
      return url;
    }
  }, []);

  /**
   * Toggle l'affichage des multiples de 10
   */
  const toggleMultiples = useCallback(() => {
    setShowMultiples((prev) => !prev);
  }, []);

  /**
   * Calcule les items à afficher dans la pagination principale
   * Retourne uniquement les pages, sans dots (les dots sont gérés manuellement dans PaginationControls)
   */
  const paginationItems = useMemo((): PaginationItem[] => {
    return pages.map((page) => ({ type: 'page' as const, page }));
  }, [pages]);

  /**
   * Calcule les multiples de 10 qui ne sont pas déjà affichés
   * Par exemple: 10, 20, 30, etc. (en 0-indexed: 9, 19, 29, etc.)
   */
  const multiplesOfTen = useMemo(() => {
    const pagesSet = new Set(pages);
    const multiples: number[] = [];

    for (let i = 10; i < realNbPages; i += 10) {
      const multiplePage = i - 1; // Convert to 0-indexed
      if (!pagesSet.has(multiplePage)) {
        multiples.push(multiplePage);
      }
    }

    return multiples;
  }, [pages, realNbPages]);

  /**
   * Détermine si la première page est dans la liste
   */
  const hasFirstPageInList = pages[0] === 0;

  /**
   * Détermine si la dernière page est dans la liste
   */
  const hasLastPageInList = pages[pages.length - 1] === realNbPages - 1;

  /**
   * Détermine si on doit afficher des dots après la première page
   */
  const showFirstPageDots = pages[0] !== 1 && !hasFirstPageInList;

  /**
   * Détermine si on doit afficher des dots avant la dernière page
   */
  const showLastPageDots =
    pages[pages.length - 1] !== realNbPages - 2 && !hasLastPageInList;

  return {
    // État de pagination
    nbPages: realNbPages,
    currentPage: currentRefinement,
    isFirstPage,
    isLastPage,

    // Items à afficher
    paginationItems,
    multiplesOfTen,
    showMultiples,

    // Flags pour l'affichage conditionnel
    hasFirstPageInList,
    hasLastPageInList,
    showFirstPageDots,
    showLastPageDots,

    // Actions
    navigateToPage,
    toggleMultiples,
    createURL,
    toRelativeURL,
  };
}
